import Card from "@rio-cloud/rio-uikit/Card";
import ContentLoader from "@rio-cloud/rio-uikit/ContentLoader";

interface TopVehiclesMaintenanceTableSkeletonProps {
  items?: number;
}

const TITLE_LINE_HEIGHT = 23;
const TITLE_WIDTH = 110;
const SUBTITLE_LINE_HEIGHT = 23;
const SUBTITLE_WIDTH = 120;
const FILTER_ICON_SIZE = 18;
const FILTER_ICON_BOX_SIZE = 24;
const HEADER_LINE_HEIGHT = 16;
const CELL_LINE_HEIGHT = 16;

const SUBTITLE_STYLE = {
  fontFamily: "Source Sans Pro",
  fontWeight: 300,
  fontStyle: "italic",
  fontSize: 14,
  lineHeight: "22.86px",
  letterSpacing: 0,
  verticalAlign: "middle",
} as const;

const FILTER_BUTTON_STYLE = {
  backgroundColor: "transparent",
  border: "none",
  padding: 4,
} as const;

const FILTER_ICON_STYLE = {
  display: "inline-grid",
  placeItems: "center",
  verticalAlign: "middle",
  width: FILTER_ICON_BOX_SIZE,
  height: FILTER_ICON_BOX_SIZE,
  fontSize: FILTER_ICON_SIZE,
  lineHeight: 1,
} as const;

const HEADER_CELL_STYLE = { fontWeight: 500, fontSize: 13 } as const;
const HEADER_CELL_NOWRAP_STYLE = { fontWeight: 500, fontSize: 13, whiteSpace: "nowrap" } as const;
const BODY_CELL_STYLE = { fontSize: 13 } as const;
const BODY_CELL_NOWRAP_STYLE = { fontSize: 13, whiteSpace: "nowrap" } as const;

const HEADER_LOADER_CLASS = "display-block skeleton-on-light-bg";
const HEADER_WIDTHS = [53, 73, 140];
const VEHICLE_WIDTHS = [70, 90, 110, 100, 95];
const AMOUNT_WIDTHS = [68, 68, 62, 62, 62];
const COST_WIDTHS = [45, 50, 45, 50, 45];

export const TopVehiclesMaintenanceSkeletonRows = ({
  items = 5,
}: Readonly<TopVehiclesMaintenanceTableSkeletonProps>) => (
  <>
    {Array.from({ length: items }, (_, index) => (
      <tr key={index}>
        <td style={BODY_CELL_NOWRAP_STYLE}>
          <ContentLoader
            className="display-block"
            height={CELL_LINE_HEIGHT}
            width={VEHICLE_WIDTHS[index % VEHICLE_WIDTHS.length]}
          />
        </td>
        <td style={BODY_CELL_STYLE}>
          <ContentLoader
            className="display-block"
            height={CELL_LINE_HEIGHT}
            width={AMOUNT_WIDTHS[index % AMOUNT_WIDTHS.length]}
          />
        </td>
        <td style={BODY_CELL_STYLE}>
          <ContentLoader
            className="display-block"
            height={CELL_LINE_HEIGHT}
            width={COST_WIDTHS[index % COST_WIDTHS.length]}
          />
        </td>
      </tr>
    ))}
  </>
);

export default function TopVehiclesMaintenanceTableSkeleton({
  items = 5,
}: Readonly<TopVehiclesMaintenanceTableSkeletonProps>) {
  return (
    <Card className="padding-y-25 padding-x-15 padding-x-25-sm display-flex flex-column height-100pct">
      <div className="display-flex flex-wrap justify-content-between align-items-center padding-bottom-20">
        <div className="display-flex flex-wrap align-items-center gap-10 min-width-0">
          <span className="text-size-16 text-bold">
            <ContentLoader className="display-block" height={TITLE_LINE_HEIGHT} width={TITLE_WIDTH} />
          </span>
          <span style={SUBTITLE_STYLE}>
            <ContentLoader className="display-block" height={SUBTITLE_LINE_HEIGHT} width={SUBTITLE_WIDTH} />
          </span>
        </div>
        <div style={{ position: "relative" }}>
          <span className="btn btn-sm" style={FILTER_BUTTON_STYLE}>
            <span style={FILTER_ICON_STYLE}>
              <ContentLoader className="display-block" height={FILTER_ICON_SIZE} width={FILTER_ICON_SIZE} />
            </span>
          </span>
        </div>
      </div>
      <div className="table-responsive">
        <table className="table table-hover" style={{ marginBottom: 0 }}>
          <thead>
            <tr className="bg-lightest">
              <th className="text-color-gray" style={HEADER_CELL_NOWRAP_STYLE}>
                <ContentLoader className={HEADER_LOADER_CLASS} height={HEADER_LINE_HEIGHT} width={HEADER_WIDTHS[0]} />
              </th>
              <th className="text-color-gray" style={HEADER_CELL_STYLE}>
                <ContentLoader className={HEADER_LOADER_CLASS} height={HEADER_LINE_HEIGHT} width={HEADER_WIDTHS[1]} />
              </th>
              <th className="text-color-gray" style={HEADER_CELL_STYLE}>
                <ContentLoader className={HEADER_LOADER_CLASS} height={HEADER_LINE_HEIGHT} width={HEADER_WIDTHS[2]} />
              </th>
            </tr>
          </thead>
          <tbody>
            <TopVehiclesMaintenanceSkeletonRows items={items} />
          </tbody>
        </table>
      </div>
    </Card>
  );
}
